import type { LiveAudienceTier } from '../../types/auth';

/**
 * CORE-879 (возврат QA 23.10) — давать ли «Поделиться», «Пригласить» и репост
 * у Live-сессии.
 *
 * Купившая программу открыла закрытую сессию, нажала «Пригласить» и получила
 * `/live/1?ref=share`. Получатель этой ссылки видит «Live-сессия не найдена»:
 * сервер пускает в сессию программы только купивших (LiveAudienceAccess), а
 * приватную — только по `?invite=`, которого у купившей нет. Мастеру ту же
 * ссылку правка №4 из 58630 уже убрала с экрана после сохранения.
 *
 * Решение — по двум фактам, а не по догадке:
 *  - сессия у человека по купленной программе (`byProgram` личного
 *    календаря). Только у таких вход может держаться на покупке; у остальных
 *    кнопка остаётся как была — и у «только подписчикам», где получатель без
 *    подписки видит путь подписаться (CORE-980), а не тупик;
 *  - открывается ли ровно та ссылка, которой делимся, у гостя без входа
 *    (`opensForGuest`, ответ сервера). Открытая сессия, вложенная в программу,
 *    так и остаётся с «Поделиться».
 *
 * Пока ответа нет, у сессии программы кнопки нет: лишняя кнопка, ведущая в
 * тупик, хуже кнопки, появившейся через мгновение.
 *
 * Владелец знает уровень своей сессии сам. Сессию программы ему тоже
 * незачем рассылать «друзьям» — та же причина, что у правки №4. Приватную
 * он отдаёт со своим кодом приглашения, и она открывается.
 *
 * Pure (no React / RN imports).
 */
export interface LiveShareGateInput {
  isOwner: boolean;
  /** Уровень своей сессии у владельца; null — ещё не загружен. */
  ownerVisibility?: LiveAudienceTier | null;
  /** Сессия у зрителя по купленной программе. */
  byProgram: boolean;
  /** Открывается ли ссылка у гостя; null — ответа ещё нет. */
  opensForGuest: boolean | null;
}

export function canShareLiveLink(input: LiveShareGateInput): boolean {
  if (input.isOwner) return input.ownerVisibility !== 'program';
  if (!input.byProgram) return true;
  return input.opensForGuest === true;
}

/**
 * Месяц личного календаря, в котором лежит сессия: `GET /api/user/my-calendar`
 * режет по месяцу в UTC. Без месяца календарь отдаёт только будущее, и идущая
 * или прошедшая сессия программы в нём уже не видна.
 */
export function calendarMonthOf(startDateTimeUtc: string | null | undefined): string | null {
  if (!startDateTimeUtc) return null;
  // Строка без пояса — это UTC сервера; `Date` прочёл бы её как местное время
  // и у полуночи первого числа увёл бы в соседний месяц.
  const zoned = /(?:[zZ]|[+-]\d\d:?\d\d)$/.test(startDateTimeUtc) ? startDateTimeUtc : `${startDateTimeUtc}Z`;
  const ms = new Date(zoned).getTime();
  if (!Number.isFinite(ms)) return null;
  return new Date(ms).toISOString().slice(0, 7);
}
